import React, {
  createContext,
  useState,
  useContext,
  useEffect,
  useCallback,
  useMemo,
} from 'react';
import { useAccountData } from './AccountContext';
import {
  getBlogPosts,
  getBlogPost,
  createBlogPost as createPost,
  updateBlogPost as updatePost,
  deleteBlogPost as deletePost,
  getComments,
  addComment as addCommentApi,
  updateComment as updateCommentApi,
  deleteComment as deleteCommentApi
} from '../utils/blogApi';
import { normalizeVisibility } from '../pages/Blog/blogVisibility';

export const BlogContext = createContext(null);

export const useBlog = () => {
  const context = useContext(BlogContext);
  if (!context) {
    console.error('useBlog must be used within a BlogProvider');
    return {
      posts: [],
      loading: false,
      error: null,
      fetchPosts: () => {},
      fetchPost: () => {},
      createPost: () => {},
      updatePost: () => {},
      deletePost: () => {},
      fetchComments: () => {},
      addComment: () => {},
      updateComment: () => {},
      deleteComment: () => {},
      isAuthenticated: false,
      user: null,
    };
  }
  return context;
};

export const BlogProvider = ({ children }) => {
  const { token, auth } = useAccountData();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchPosts = useCallback(async (silent = false) => {
    if (!silent) {
      setLoading(true);
      setError(null);
    }
    try {
      const data = await getBlogPosts(token);
      const list = data?.posts ?? data;
      const raw = Array.isArray(list) ? list : [];
      setPosts(raw.map(normalizeVisibility));
    } catch (err) {
      if (!silent) setError(err.message || 'Failed to fetch blog posts');
      console.error('Error fetching posts:', err);
    } finally {
      if (!silent) setLoading(false);
    }
  }, [token]);

  const fetchPost = useCallback(async (postId) => {
    setLoading(true);
    setError(null);
    try {
      const data = await getBlogPost(postId, token);
      const post = data.post || data;
      return post ? normalizeVisibility(post) : post;
    } catch (err) {
      setError(err.message || 'Failed to fetch blog post');
      console.error('Error fetching post:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [token]);

  const createPostHandler = useCallback(async (postData) => {
    if (!token) {
      throw new Error('Authentication required');
    }
    setLoading(true);
    setError(null);
    try {
      const newPost = await createPost(postData, token);
      const resolved = newPost?.post ?? newPost?.data ?? newPost;
      if (resolved && typeof resolved === 'object') {
        setPosts((prev) => [normalizeVisibility(resolved), ...prev.filter(Boolean)]);
      }
      await fetchPosts(true);
      return resolved;
    } catch (err) {
      setError(err.message || 'Failed to create blog post');
      console.error('Error creating post:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [token, fetchPosts]);

  const updatePostHandler = useCallback(async (postId, postData) => {
    if (!token) {
      throw new Error('Authentication required');
    }
    setLoading(true);
    setError(null);
    try {
      const updatedPost = await updatePost(postId, postData, token);
      const raw = updatedPost?.post ?? updatedPost?.data ?? updatedPost;
      const resolved =
        raw && typeof raw === 'object' ? normalizeVisibility(raw) : null;
      setPosts((prev) =>
        prev.map((post) => {
          if (post._id !== postId && post.id !== postId) return post;
          if (!resolved) {
            return normalizeVisibility({
              ...post,
              ...postData,
              _id: post._id ?? postId,
              id: post.id ?? postId,
            });
          }
          return {
            ...post,
            ...resolved,
            visibility:
              resolved.visibility ?? postData.visibility ?? post.visibility,
            allowedViewers:
              resolved.allowedViewers ??
              resolved.allowed_viewers ??
              post.allowedViewers ??
              post.allowed_viewers,
          };
        })
      );
      await fetchPosts(true);
      return resolved;
    } catch (err) {
      setError(err.message || 'Failed to update blog post');
      console.error('Error updating post:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [token, fetchPosts]);

  const deletePostHandler = useCallback(async (postId) => {
    if (!token) {
      throw new Error('Authentication required');
    }
    setLoading(true);
    setError(null);
    try {
      await deletePost(postId, token);
      setPosts(prev => prev.filter(post => (post._id !== postId && post.id !== postId)));
    } catch (err) {
      setError(err.message || 'Failed to delete blog post');
      console.error('Error deleting post:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [token]);

  const fetchComments = useCallback(async (postId) => {
    try {
      const data = await getComments(postId, token);
      return data.comments || data || [];
    } catch (err) {
      console.error('Error fetching comments:', err);
      throw err;
    }
  }, [token]);

  const addCommentHandler = useCallback(async (postId, commentData) => {
    try {
      const newComment = await addCommentApi(postId, commentData, token);
      fetchPosts(true);
      return newComment.comment || newComment;
    } catch (err) {
      console.error('Error adding comment:', err);
      throw err;
    }
  }, [token, fetchPosts]);

  const updateCommentHandler = useCallback(async (postId, commentId, commentData) => {
    if (!token) {
      throw new Error('Authentication required to update comment');
    }
    try {
      const updatedComment = await updateCommentApi(postId, commentId, commentData, token);
      fetchPosts(true);
      return updatedComment.comment || updatedComment;
    } catch (err) {
      console.error('Error updating comment:', err);
      throw err;
    }
  }, [token, fetchPosts]);

  const deleteCommentHandler = useCallback(async (postId, commentId) => {
    if (!token) {
      throw new Error('Authentication required to delete comment');
    }
    try {
      await deleteCommentApi(postId, commentId, token);
      fetchPosts(true);
    } catch (err) {
      console.error('Error deleting comment:', err);
      throw err;
    }
  }, [token, fetchPosts]);

  useEffect(() => {
    fetchPosts();
  }, [fetchPosts]);

  const contextValue = useMemo(
    () => ({
      posts,
      loading,
      error,
      fetchPosts,
      fetchPost,
      createPost: createPostHandler,
      updatePost: updatePostHandler,
      deletePost: deletePostHandler,
      fetchComments,
      addComment: addCommentHandler,
      updateComment: updateCommentHandler,
      deleteComment: deleteCommentHandler,
      isAuthenticated: !!token && !!auth,
      user: auth,
    }),
    [
      posts,
      loading,
      error,
      fetchPosts,
      fetchPost,
      createPostHandler,
      updatePostHandler,
      deletePostHandler,
      fetchComments,
      addCommentHandler,
      updateCommentHandler,
      deleteCommentHandler,
      token,
      auth,
    ]
  );

  return (
    <BlogContext.Provider value={contextValue}>
      {children}
    </BlogContext.Provider>
  );
};
